import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Paperclip, X, FileText, Loader2 } from 'lucide-react'
import { cn } from '@/utils'

interface Attachment {
  file_id: string
  filename: string
  content_type: string
  size: number
  is_image: boolean
  preview?: string
}

interface ChatInputProps {
  onSend: (message: string, attachments?: Attachment[]) => void
  disabled?: boolean
  placeholder?: string
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Message Niki...',
}: ChatInputProps) {
  const [message, setMessage] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`
    }
  }, [message])

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const token = localStorage.getItem('niki_access_token')
    if (!file || !token) return

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Upload failed')
      }

      const data = await response.json()

      // Create preview for images
      let preview: string | undefined
      if (data.is_image) {
        preview = URL.createObjectURL(file)
      }

      setAttachments(prev => [...prev, { ...data, preview }])
    } catch (error) {
      console.error('Upload error:', error)
      // TODO: Show toast notification
    } finally {
      setIsUploading(false)
    }
  }

  const removeAttachment = (fileId: string) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.file_id === fileId)
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview)
      }
      return prev.filter(a => a.file_id !== fileId)
    })
  }

  const handleSubmit = () => {
    if ((message.trim() || attachments.length > 0) && !disabled) {
      onSend(message.trim(), attachments.length > 0 ? attachments : undefined)
      setMessage('')
      setAttachments([])
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const canSend = (message.trim() || attachments.length > 0) && !disabled && !isUploading

  return (
    <div className="p-4 border-t border-white/10 glass-light safe-area-bottom">
      {/* Attachments Preview */}
      <AnimatePresence>
        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-3 flex flex-wrap gap-2"
          >
            {attachments.map((attachment) => (
              <motion.div
                key={attachment.file_id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="relative group"
              >
                {attachment.is_image && attachment.preview ? (
                  <div className="w-20 h-20 rounded-xl overflow-hidden border border-white/20">
                    <img
                      src={attachment.preview}
                      alt={attachment.filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-white/10 border border-white/20 flex flex-col items-center justify-center p-2">
                    <FileText className="w-6 h-6 text-white/60 mb-1" />
                    <span className="text-[10px] text-white/50 truncate w-full text-center">
                      {attachment.filename.split('.').pop()?.toUpperCase()}
                    </span>
                  </div>
                )}
                {/* Remove button */}
                <button
                  onClick={() => removeAttachment(attachment.file_id)}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-end gap-3 p-2 rounded-2xl bg-white/5 border border-white/10">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
          onChange={handleFileSelect}
        />

        {/* Attachment button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={cn(
            'p-2 transition-colors rounded-lg hover:bg-white/5',
            isUploading ? 'text-gold animate-pulse' : 'text-white/40 hover:text-white/70'
          )}
          aria-label="Attach file"
        >
          {isUploading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Paperclip className="w-5 h-5" />
          )}
        </button>

        {/* Input */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={attachments.length > 0 ? 'Add a message (optional)...' : placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'flex-1 bg-transparent resize-none outline-none',
            'text-[15px] text-white',
            'placeholder:text-white/40',
            'min-h-[24px] max-h-[150px] py-2',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        />

        {/* Send button */}
        <motion.button
          onClick={handleSubmit}
          disabled={!canSend}
          whileHover={canSend ? { scale: 1.05 } : undefined}
          whileTap={canSend ? { scale: 0.95 } : undefined}
          className={cn(
            'p-3 rounded-xl transition-all',
            canSend
              ? 'bg-gradient-to-br from-gold to-gold-dark text-white shadow-lg shadow-gold/30'
              : 'bg-white/10 text-white/30 cursor-not-allowed'
          )}
          style={
            canSend
              ? {
                  boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                }
              : undefined
          }
          aria-label="Send message"
        >
          <Send className="w-5 h-5" />
        </motion.button>
      </div>

      <p className="text-center text-[11px] text-white/30 mt-2">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  )
}
